import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { CnInput, CnButton, CnPopup } from '@sto/cn-ui';
import styles from './index.module.scss';
import moment from 'moment';
import { TimeDimension, TimeRange, isSameDate, getRangeFromDate, formatDate, formatDateWithCustomFormat } from './utils';

/**
 * 时间选择器
 */
export interface TimePickerProps {
  /** 默认选中的时间维度 */
  defaultDimension?: TimeDimension;
  /** 默认选中的时间范围 */
  defaultRange?: TimeRange;
  /** 时间变化回调 */
  onChange?: (data: { dimension: TimeDimension, range: TimeRange, value: string }) => void;
  /** 是否禁用 */
  disabled?: boolean;
  /** 自定义类名 */
  className?: string;
  /** 是否显示 */
  visible?: boolean;
  /** 关闭回调 */
  onClose?: () => void;
  /** 数据源 */
  dataSource?: Array<{ value: TimeDimension; label: string }>;
  /** 默认值，支持 Date 对象或日期字符串 */
  defaultValue?: Date | string;
  /** 受控值，支持 Date 对象或日期字符串 */
  value?: Date | string;
  /** 输入格式，用于解析字符串日期 */
  format?: string;
  /** 输出格式 */
  outputFormat?: string;
  /** 占位符文本 */
  placeholder?: string;
}

/**
 * 面板显示模式
 */
type PanelMode = 'calendar' | 'year' | 'month';

/**
 * 获取维度配置(默认配置)
 */
const getDimensionConfig = [
  {
    value: 'day' as TimeDimension,
    label: '日',
  },
  {
    value: 'week' as TimeDimension,
    label: '周',
  },
  {
    value: 'month' as TimeDimension,
    label: '月',
  },
  {
    value: 'quarter' as TimeDimension,
    label: '季',
  }
];

/**
 * 时间选择器，可以在页面上选择不同维度日期(日、周、月、季)
 */
const TimePicker: React.FC<TimePickerProps> = ({
  defaultDimension = 'day',
  onChange,
  disabled = false,
  className = '',
  dataSource,
  defaultValue,
  value,
  format,
  outputFormat,
  ...props
}) => {
  const [currentDimension, setCurrentDimension] = useState<TimeDimension>(defaultDimension); // 当前选中的维度
  const [currentDate, setCurrentDate] = useState(new Date()); // 当前展示的面板日期
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined); // 选中的日历值
  const [lastConfirmedDate, setLastConfirmedDate] = useState<Date | undefined>(undefined); // 上一次确认的日期
  const [visible, setVisible] = useState(false);
  const [panelMode, setPanelMode] = useState<PanelMode>('calendar');

  // 受控值变化
  useEffect(() => {
    if (value) {
      const dateValue = typeof value === 'string' ? new Date(value) : value;
      if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
        setSelectedDate(dateValue);
        setCurrentDate(dateValue);
        setLastConfirmedDate(dateValue); // 同步更新已确认的日期
      }
    }
  }, [value]);

  // 初始化默认值
  useEffect(() => {
    if (defaultValue) {
      const dateValue = typeof defaultValue === 'string' ? new Date(defaultValue) : defaultValue;
      if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
        setSelectedDate(dateValue);
        setCurrentDate(dateValue);
        setLastConfirmedDate(dateValue); // 设置初始已确认的日期
      }
    }
  }, [defaultValue]);

  // 处理维度切换
  const handleDimensionChange = (dimension: TimeDimension) => {
    if (disabled) return;
    setCurrentDimension(dimension);
  };

  // 处理日期选择
  const handleDateSelect = (date: Date) => {
    if (disabled) return;
    setSelectedDate(date);
  };

  // 月份导航
  const handleMonthChange = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  // 年份导航
  const handleYearChange = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setFullYear(newDate.getFullYear() - 1);
    } else {
      newDate.setFullYear(newDate.getFullYear() + 1);
    }
    setCurrentDate(newDate);
  };

  // 处理年选择
  const handleYearSelect = (year: number) => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(year);
    setCurrentDate(newDate);
    setPanelMode('calendar'); // 直接回到日历视图
  };

  // 处理月选择
  const handleMonthSelect = (month: number) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(month);
    setCurrentDate(newDate);
    setPanelMode('calendar'); // 直接回到日历视图
  };

  // 获取日历数据
  const getCalendarData = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // 获取当月第一天和最后一天
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // 获取上个月的最后几天
    // 调整为周一开始：getDay()返回0-6（周日到周六），需要转换为1-7（周一到周日）
    let firstDayOfWeek = firstDay.getDay();
    if (firstDayOfWeek === 0) firstDayOfWeek = 7; // 周日转换为7
    const prevMonthDays = firstDayOfWeek - 1; // 周一开始，所以减1
    
    // 获取下个月的前几天
    let lastDayOfWeek = lastDay.getDay();
    if (lastDayOfWeek === 0) lastDayOfWeek = 7; // 周日转换为7
    const nextMonthDays = 7 - lastDayOfWeek;
    
    const calendar = [];

    const compareDate = selectedDate || new Date();
    
    // 添加上个月的日期
    for (let i = prevMonthDays - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, new Date(year, month, 0).getDate() - i);
      calendar.push({
        date,
        isCurrentMonth: false,
        isSelected: isSameDate(date, compareDate),
        isToday: isSameDate(date, new Date())
      });
    }
    
    // 添加当月的日期
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      calendar.push({
        date,
        isCurrentMonth: true,
        isSelected: isSameDate(date, compareDate),
        isToday: isSameDate(date, new Date())
      });
    }
    
    // 添加下个月的日期
    for (let i = 1; i <= nextMonthDays; i++) {
      const date = new Date(year, month + 1, i);
      calendar.push({
        date,
        isCurrentMonth: false,
        isSelected: isSameDate(date, compareDate),
        isToday: isSameDate(date, new Date())
      });
    }
    
    return calendar;
  };

  // 获取年选择数据
  const getYearData = () => {
    const currentYear = currentDate.getFullYear();
    const decadeStart = Math.floor(currentYear / 10) * 10;
    const years = [];
    
    for (let i = -1; i <= 10; i++) {
      const year = decadeStart + i;
      years.push({
        year,
        isCurrentDecade: year >= decadeStart && year < decadeStart + 10,
        isCurrentYear: year === currentYear
      });
    }
    
    return years;
  };

  // 获取月选择数据
  const getMonthData = () => {
    const months = [];
    for (let i = 0; i < 12; i++) {
      months.push({
        month: i,
        label: `${i + 1}月`,
        isCurrentMonth: i === currentDate.getMonth()
      });
    }
    return months;
  };

  /**
   * 维度配置
   */
  const dimensionConfig = useMemo(() => {
    if (dataSource && dataSource.length) {
      const firstItem = dataSource[0].value;
      setCurrentDimension(firstItem);
      return dataSource;
    }
    return getDimensionConfig;
  }, [dataSource]);

  const calendarData = getCalendarData();
  const yearData = getYearData();
  const monthData = getMonthData();

  /**
   * 输入框变化
   */
  const onInputChange = useCallback((value) => {
    if (!value) {
      onChange?.(null);
      setSelectedDate(undefined);
    }
  }, []);

  /**
   * 格式化输入框值
   */
  const formatInputValue = useMemo(() => {
    if (selectedDate) {
      return moment(selectedDate).format(format);
    } else {
      return '';
    }
  }, [selectedDate, format])

  return (
    <CnPopup
      v2
      visible={visible}
      trigger={
        <CnInput 
          placeholder="请选择日期"
          value={formatInputValue}
          onChange={onInputChange}
          {...props}
        /> 
      }
      triggerType="click"
      triggerClickKeycode={[32, 40]}
      needAdjust
      onVisibleChange={(flag) => setVisible(flag)}
    >
      <div className={`${styles.timePicker} ${className}`} onClick={(e) => e.stopPropagation()}>
        {/* 标题 */}
        <div className={styles.title}>选择日期</div>
        
        {/* 时间维度选择器 */}
        <div className={styles.dimensionSelectorContainer}>
          <div className={styles.dimensionSelector}>
            {dimensionConfig.map((config) => (
              <div
                key={config.value}
                className={`${styles.dimensionItem} ${
                  currentDimension === config.value ? styles.active : ''
                } ${disabled ? styles.disabled : ''}`}
                onClick={() => handleDimensionChange(config.value)}
              >
                <div className={styles.dimensionLabel}>{config.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* 日历导航 */}
        {panelMode === 'calendar' && <div className={styles.calendarHeader}>
          <div className={styles.navigation}>
            <button 
              className={styles.navButton}
              onClick={() => handleYearChange('prev')}
              disabled={disabled}
            >
              <svg height="1em" viewBox="0 0 44 44"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-24.000000, -22.000000)"><g transform="translate(24.000000, 22.000000)"><rect x="0" y="0" width="44" height="44"></rect><g transform="translate(7.000000, 4.000000)" fill="currentColor" fill-rule="nonzero"><path d="M19.4833058,2.71985611 L3.53051139,17.0699744 C3.0173831,17.5315665 2.97522952,18.3220903 3.43630803,18.8357433 L3.43630796,18.8357432 C3.46601289,18.8688164 3.49745845,18.9002801 3.53051133,18.9300007 L19.4833057,33.2801611 C20.1234001,33.8559077 20.1759552,34.8420707 19.6007967,35.4827774 C19.0256382,36.1235263 18.0404824,36.1761351 17.400388,35.6003885 L1.44759367,21.2502703 L1.4475933,21.25027 C1.33208743,21.1463692 1.22220259,21.036372 1.11840792,20.920748 C-0.49302969,19.1256817 -0.345639536,16.3628317 1.4475933,14.7497465 L17.4003877,0.399628282 C18.0404821,-0.176160428 19.0256378,-0.123509422 19.6007963,0.517239417 C20.1759548,1.1579461 20.1233997,2.14410915 19.4833053,2.7198557 L19.4833058,2.71985611 Z"></path><path d="M19.5305114,17.0699744 C19.0173831,17.5315665 18.9752295,18.3220903 19.436308,18.8357433 C19.4660129,18.8688164 19.4974585,18.9002801 19.5305113,18.9300007 L29.4833057,27.2801611 C30.1234001,27.8559077 30.1759552,28.8420707 29.6007967,29.4827774 C29.0256382,30.1235263 28.0404824,30.1761351 27.400388,29.6003885 L17.4475937,21.2502703 C17.3320874,21.1463692 17.2222026,21.036372 17.1184079,20.920748 C15.5069703,19.1256817 15.6543605,19.1256817 17.4475933,14.7497465 L27.4003877,6.39962828 C28.0404821,5.82383957 29.0256378,5.87649058 29.6007963,6.51723942 C30.1759548,7.1579461 30.1233997,8.14410915 29.4833053,8.7198557 L19.5305114,17.0699744 Z"></path></g></g></g></g></svg>
            </button>
            <button 
              className={styles.navButton}
              onClick={() => handleMonthChange('prev')}
              disabled={disabled}
            >
            <svg height="1em" viewBox="0 0 44 44"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-100.000000, -22.000000)"><g transform="translate(100.000000, 22.000000)"><rect x="0" y="0" width="44" height="44"></rect><g transform="translate(12.000000, 4.000000)" fill="currentColor" fill-rule="nonzero"><path d="M19.4833058,2.71985611 L3.53051139,17.0699744 C3.0173831,17.5315665 2.97522952,18.3220903 3.43630803,18.8357433 L3.43630796,18.8357432 C3.46601289,18.8688164 3.49745845,18.9002801 3.53051133,18.9300007 L19.4833057,33.2801611 C20.1234001,33.8559077 20.1759552,34.8420707 19.6007967,35.4827774 C19.0256382,36.1235263 18.0404824,36.1761351 17.400388,35.6003885 L1.44759367,21.2502703 L1.4475933,21.25027 C1.33208743,21.1463692 1.22220259,21.036372 1.11840792,20.920748 C-0.49302969,19.1256817 -0.345639536,16.3628317 1.4475933,14.7497465 L17.4003877,0.399628282 C18.0404821,-0.176160428 19.0256378,-0.123509422 19.6007963,0.517239417 C20.1759548,1.1579461 20.1233997,2.14410915 19.4833053,2.7198557 L19.4833058,2.71985611 Z"></path></g></g></g></g></svg>
            </button>
          </div>
          
          <div className={styles.currentMonth}>
            <span 
              className={styles.yearClickable}
              onClick={() => setPanelMode('year')}
            >
              {currentDate.getFullYear()}
            </span>
            /
            <span 
              className={styles.monthClickable}
              onClick={() => setPanelMode('month')}
            >
              {String(currentDate.getMonth() + 1).length === 1 ? '0' + String(currentDate.getMonth() + 1) : String(currentDate.getMonth() + 1)}
            </span>
          </div>
          
          <div className={`${styles.navigation} ${styles.nextBtnBox}`}>
            <button 
              className={styles.navButton}
              onClick={() => handleMonthChange('next')}
              disabled={disabled}
            >
              <svg height="1em" viewBox="0 0 44 44"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-100.000000, -22.000000)"><g transform="translate(100.000000, 22.000000)"><rect x="0" y="0" width="44" height="44"></rect><g transform="translate(12.000000, 4.000000)" fill="currentColor" fill-rule="nonzero"><path d="M19.4833058,2.71985611 L3.53051139,17.0699744 C3.0173831,17.5315665 2.97522952,18.3220903 3.43630803,18.8357433 L3.43630796,18.8357432 C3.46601289,18.8688164 3.49745845,18.9002801 3.53051133,18.9300007 L19.4833057,33.2801611 C20.1234001,33.8559077 20.1759552,34.8420707 19.6007967,35.4827774 C19.0256382,36.1235263 18.0404824,36.1761351 17.400388,35.6003885 L1.44759367,21.2502703 L1.4475933,21.25027 C1.33208743,21.1463692 1.22220259,21.036372 1.11840792,20.920748 C-0.49302969,19.1256817 -0.345639536,16.3628317 1.4475933,14.7497465 L17.4003877,0.399628282 C18.0404821,-0.176160428 19.0256378,-0.123509422 19.6007963,0.517239417 C20.1759548,1.1579461 20.1233997,2.14410915 29.4833053,2.7198557 L19.4833058,2.71985611 Z"></path></g></g></g></g></svg>
            </button>
            <button 
              className={styles.navButton}
              onClick={() => handleYearChange('next')}
              disabled={disabled}
            >
            <svg height="1em" viewBox="0 0 44 44"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-24.000000, -22.000000)"><g transform="translate(24.000000, 22.000000)"><rect x="0" y="0" width="44" height="44"></rect><g transform="translate(7.000000, 4.000000)" fill="currentColor" fill-rule="nonzero"><path d="M19.4833058,2.71985611 L3.53051139,17.0699744 C3.0173831,17.5315665 2.97522952,18.3220903 3.43630803,18.8357433 L3.43630796,18.8357432 C3.46601289,18.8688164 3.49745845,18.9002801 3.53051133,18.9300007 L19.4833057,33.2801611 C20.1234001,33.8559077 20.1759552,34.8420707 19.6007967,35.4827774 C19.0256382,36.1235263 18.0404824,36.1761351 17.400388,35.6003885 L1.44759367,21.2502703 L1.4475933,21.25027 C1.33208743,21.1463692 1.22220259,21.036372 1.11840792,20.920748 C-0.49302969,19.1256817 -0.345639536,16.3628317 1.4475933,14.7497465 L17.4003877,0.399628282 C18.0404821,-0.176160428 19.0256378,-0.123509422 19.6007963,0.517239417 C20.1759548,1.1579461 20.1233997,2.14410915 19.4833053,2.7198557 L19.4833058,2.71985611 Z"></path><path d="M19.5305114,17.0699744 C19.0173831,17.5315665 18.9752295,18.3220903 19.436308,18.8357433 C19.4660129,18.8688164 19.4974585,18.9002801 19.5305113,18.9300007 L29.4833057,27.2801611 C30.1234001,27.8559077 30.1759552,28.8420707 29.6007967,29.4827774 C29.0256382,30.1235263 28.0404824,30.1761351 27.400388,29.6003885 L17.4475937,21.2502703 C17.3320874,21.1463692 17.2222026,21.036372 17.1184079,20.920748 C15.5069703,19.1256817 15.6543605,19.1256817 17.4475933,14.7497465 L27.4003877,6.39962828 C28.0404821,5.82383957 29.0256378,5.87649058 29.6007963,6.51723942 C30.1759548,7.1579461 30.1233997,8.14410915 29.4833053,8.7198557 L19.5305114,17.0699744 Z"></path></g></g></g></g></svg>
            </button>
          </div>
        </div>}

        {/* 日历内容区域 */}
        {panelMode === 'calendar' && (
          <div className={styles.calendar}>
            {/* 星期标题 */}
            <div className={styles.weekHeader}>
              {['一', '二', '三', '四', '五', '六', '日'].map((day, index) => (
                <div key={day} className={styles.weekDay}>{day}</div>
              ))}
            </div>
            
            {/* 日期网格 */}
            <div className={styles.dateGrid}>
              {calendarData.map((item, index) => (
                <div
                  key={index}
                  className={`${styles.dateItem} ${
                    !item.isCurrentMonth ? styles.otherMonth : ''
                  } ${item.isSelected ? styles.selected : ''} ${
                    item.isToday ? styles.today : ''
                  }`}
                  onClick={() => handleDateSelect(item.date)}
                >
                  {item.date.getDate()}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 年份选择面板 */}
        {panelMode === 'year' && (
          <div className={styles.yearPanel}>
            {/* 年份导航 */}
            <div className={styles.yearHeader}>
              <button 
                className={styles.navButton}
                onClick={() => {
                  const newDate = new Date(currentDate);
                  newDate.setFullYear(newDate.getFullYear() - 10);
                  setCurrentDate(newDate);
                }}
                disabled={disabled}
              >
                <svg height="1em" viewBox="0 0 44 44"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-24.000000, -22.000000)"><g transform="translate(24.000000, 22.000000)"><rect x="0" y="0" width="44" height="44"></rect><g transform="translate(7.000000, 4.000000)" fill="currentColor" fill-rule="nonzero"><path d="M19.4833058,2.71985611 L3.53051139,17.0699744 C3.0173831,17.5315665 2.97522952,18.3220903 3.43630803,18.8357433 L3.43630796,18.8357432 C3.46601289,18.8688164 3.49745845,18.9002801 3.53051133,18.9300007 L19.4833057,33.2801611 C20.1234001,33.8559077 20.1759552,34.8420707 19.6007967,35.4827774 C19.0256382,36.1235263 18.0404824,36.1761351 17.400388,35.6003885 L1.44759367,21.2502703 L1.4475933,21.25027 C1.33208743,21.1463692 1.22220259,21.036372 1.11840792,20.920748 C-0.49302969,19.1256817 -0.345639536,16.3628317 1.4475933,14.7497465 L17.4003877,0.399628282 C18.0404821,-0.176160428 19.0256378,-0.123509422 19.6007963,0.517239417 C20.1759548,1.1579461 20.1233997,2.14410915 19.4833053,2.7198557 L19.4833058,2.71985611 Z"></path><path d="M19.5305114,17.0699744 C19.0173831,17.5315665 18.9752295,18.3220903 19.436308,18.8357433 C19.4660129,18.8688164 19.4974585,18.9002801 19.5305113,18.9300007 L29.4833057,27.2801611 C30.1234001,27.8559077 30.1759552,28.8420707 29.6007967,29.4827774 C29.0256382,30.1235263 28.0404824,30.1761351 27.400388,29.6003885 L17.4475937,21.2502703 C17.3320874,21.1463692 17.2222026,21.036372 17.1184079,20.920748 C15.5069703,19.1256817 15.6543605,19.1256817 17.4475933,14.7497465 L27.4003877,6.39962828 C28.0404821,5.82383957 29.0256378,5.87649058 29.6007963,6.51723942 C30.1759548,7.1579461 30.1233997,8.14410915 29.4833053,8.7198557 L19.5305114,17.0699744 Z"></path></g></g></g></g></svg>
              </button>
              <div className={styles.yearRange}>
                {Math.floor(currentDate.getFullYear() / 10) * 10}-{Math.floor(currentDate.getFullYear() / 10) * 10 + 9}
              </div>
              <button 
                className={`${styles.navButton} ${styles.nextBtnBox}`}
                onClick={() => {
                  const newDate = new Date(currentDate);
                  newDate.setFullYear(newDate.getFullYear() + 10);
                  setCurrentDate(newDate);
                }}
                disabled={disabled}
              >
                <svg height="1em" viewBox="0 0 44 44"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-24.000000, -22.000000)"><g transform="translate(24.000000, 22.000000)"><rect x="0" y="0" width="44" height="44"></rect><g transform="translate(7.000000, 4.000000)" fill="currentColor" fill-rule="nonzero"><path d="M19.4833058,2.71985611 L3.53051139,17.0699744 C3.0173831,17.5315665 2.97522952,18.3220903 3.43630803,18.8357433 L3.43630796,18.8357432 C3.46601289,18.8688164 3.49745845,18.9002801 3.53051133,18.9300007 L19.4833057,33.2801611 C20.1234001,33.8559077 20.1759552,34.8420707 19.6007967,35.4827774 C19.0256382,36.1235263 18.0404824,36.1761351 17.400388,35.6003885 L1.44759367,21.2502703 L1.4475933,21.25027 C1.33208743,21.1463692 1.22220259,21.036372 1.11840792,20.920748 C-0.49302969,19.1256817 -0.345639536,16.3628317 1.4475933,14.7497465 L17.4003877,0.399628282 C18.0404821,-0.176160428 19.0256378,-0.123509422 19.6007963,0.517239417 C20.1759548,1.1579461 20.1233997,2.14410915 19.4833053,2.7198557 L19.4833058,2.71985611 Z"></path><path d="M19.5305114,17.0699744 C19.0173831,17.5315665 18.9752295,18.3220903 19.436308,18.8357433 C19.4660129,18.8688164 19.4974585,18.9002801 19.5305113,18.9300007 L29.4833057,27.2801611 C30.1234001,27.8559077 30.1759552,28.8420707 29.6007967,29.4827774 C29.0256382,30.1235263 28.0404824,30.1761351 27.400388,29.6003885 L17.4475937,21.2502703 C17.3320874,21.1463692 17.2222026,21.036372 17.1184079,20.920748 C15.5069703,19.1256817 15.6543605,19.1256817 17.4475933,14.7497465 L27.4003877,6.39962828 C28.0404821,5.82383957 29.0256378,5.87649058 29.6007963,6.51723942 C30.1759548,7.1579461 30.1233997,8.14410915 29.4833053,8.7198557 L19.5305114,17.0699744 Z"></path></g></g></g></g></svg>
              </button>
            </div>
            <div className={styles.yearGrid}>
              {yearData.map((item, index) => (
                <div
                  key={index}
                  className={`${styles.yearItem} ${
                    !item.isCurrentDecade ? styles.otherDecade : ''
                  } ${item.isCurrentYear ? styles.currentYear : ''}`}
                  onClick={() => handleYearSelect(item.year)}
                >
                  {item.year}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 月份选择面板 */}
        {panelMode === 'month' && (
          <div className={styles.monthPanel}>
            <div className={styles.monthHeader}>
              <button 
                className={styles.navButton}
                onClick={() => {
                  const newDate = new Date(currentDate);
                  newDate.setFullYear(newDate.getFullYear() - 1);
                  setCurrentDate(newDate);
                }}
                disabled={disabled}
              >
                <svg height="1em" viewBox="0 0 44 44"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-24.000000, -22.000000)"><g transform="translate(24.000000, 22.000000)"><rect x="0" y="0" width="44" height="44"></rect><g transform="translate(7.000000, 4.000000)" fill="currentColor" fill-rule="nonzero"><path d="M19.4833058,2.71985611 L3.53051139,17.0699744 C3.0173831,17.5315665 2.97522952,18.3220903 3.43630803,18.8357433 L3.43630796,18.8357432 C3.46601289,18.8688164 3.49745845,18.9002801 3.53051133,18.9300007 L19.4833057,33.2801611 C20.1234001,33.8559077 20.1759552,34.8420707 19.6007967,35.4827774 C19.0256382,36.1235263 18.0404824,36.1761351 17.400388,35.6003885 L1.44759367,21.2502703 L1.4475933,21.25027 C1.33208743,21.1463692 1.22220259,21.036372 1.11840792,20.920748 C-0.49302969,19.1256817 -0.345639536,16.3628317 1.4475933,14.7497465 L17.4003877,0.399628282 C18.0404821,-0.176160428 19.0256378,-0.123509422 19.6007963,0.517239417 C20.1759548,1.1579461 20.1233997,2.14410915 19.4833053,2.7198557 L19.4833058,2.71985611 Z"></path><path d="M19.5305114,17.0699744 C19.0173831,17.5315665 18.9752295,18.3220903 19.436308,18.8357433 C19.4660129,18.8688164 19.4974585,18.9002801 19.5305113,18.9300007 L29.4833057,27.2801611 C30.1234001,27.8559077 30.1759552,28.8420707 29.6007967,29.4827774 C29.0256382,30.1235263 28.0404824,30.1761351 27.400388,29.6003885 L17.4475937,21.2502703 C17.3320874,21.1463692 17.2222026,21.036372 17.1184079,20.920748 C15.5069703,19.1256817 15.6543605,19.1256817 17.4475933,14.7497465 L27.4003877,6.39962828 C28.0404821,5.82383957 29.0256378,5.87649058 29.6007963,6.51723942 C30.1759548,7.1579461 30.1233997,8.14410915 29.4833053,8.7198557 L19.5305114,17.0699744 Z"></path></g></g></g></g></svg>
              </button>
              <div className={styles.currentYear}>
                {currentDate.getFullYear()}年
              </div>
              <button 
                className={`${styles.navButton} ${styles.nextBtnBox}`}
                onClick={() => {
                  const newDate = new Date(currentDate);
                  newDate.setFullYear(newDate.getFullYear() + 1);
                  setCurrentDate(newDate);
                }}
                disabled={disabled}
              >
                <svg height="1em" viewBox="0 0 44 44"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-24.000000, -22.000000)"><g transform="translate(24.000000, 22.000000)"><rect x="0" y="0" width="44" height="44"></rect><g transform="translate(7.000000, 4.000000)" fill="currentColor" fill-rule="nonzero"><path d="M19.4833058,2.71985611 L3.53051139,17.0699744 C3.0173831,17.5315665 2.97522952,18.3220903 3.43630803,18.8357433 L3.43630796,18.8357432 C3.46601289,18.8688164 3.49745845,18.9002801 3.53051133,18.9300007 L19.4833057,33.2801611 C20.1234001,33.8559077 20.1759552,34.8420707 19.6007967,35.4827774 C19.0256382,36.1235263 18.0404824,36.1761351 17.400388,35.6003885 L1.44759367,21.2502703 L1.4475933,21.25027 C1.33208743,21.1463692 1.22220259,21.036372 1.11840792,20.920748 C-0.49302969,19.1256817 -0.345639536,16.3628317 1.4475933,14.7497465 L17.4003877,0.399628282 C18.0404821,-0.176160428 19.0256378,-0.123509422 19.6007963,0.517239417 C20.1759548,1.1579461 20.1233997,2.14410915 19.4833053,2.7198557 L19.4833058,2.71985611 Z"></path><path d="M19.5305114,17.0699744 C19.0173831,17.5315665 18.9752295,18.3220903 19.436308,18.8357433 C19.4660129,18.8688164 19.4974585,18.9002801 19.5305113,18.9300007 L29.4833057,27.2801611 C30.1234001,27.8559077 30.1759552,28.8420707 29.6007967,29.4827774 C29.0256382,30.1235263 28.0404824,30.1761351 27.400388,29.6003885 L17.4475937,21.2502703 C17.3320874,21.1463692 17.2222026,21.036372 17.1184079,20.920748 C15.5069703,19.1256817 15.6543605,19.1256817 17.4475933,14.7497465 L27.4003877,6.39962828 C28.0404821,5.82383957 29.0256378,5.87649058 29.6007963,6.51723942 C30.1759548,7.1579461 30.1233997,8.14410915 29.4833053,8.7198557 L19.5305114,17.0699744 Z"></path></g></g></g></g></svg>
              </button>
            </div>
            <div className={styles.monthGrid}>
              {monthData.map((item, index) => (
                <div
                  key={index}
                  className={`${styles.monthItem} ${
                    item.isCurrentMonth ? styles.currentMonth : ''
                  }`}
                  onClick={() => handleMonthSelect(item.month)}
                >
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 底部按钮 */}
        <div className={styles.footer}>
          <CnButton 
            type="secondary"
            size="small"
            onClick={() => {
              // 取消时恢复到上一次确认的日期
              if (lastConfirmedDate) {
                setSelectedDate(lastConfirmedDate);
              }
              setVisible(false);
            }}
          >
            取消
          </CnButton>
          <CnButton 
            type="primary"
            size="small"
            onClick={() => {
              const checkedDate = selectedDate || new Date();
              const range = getRangeFromDate(checkedDate, currentDimension);
              let formatSelecteDate = formatDate(checkedDate);
              if (outputFormat) {
                // 简单的格式转换，支持常见的输出格式
                formatSelecteDate = formatDateWithCustomFormat(checkedDate, outputFormat);
              }
              const dimensionText = dimensionConfig?.find(item => item.value === currentDimension)?.label;
              const data = { dimension: currentDimension, dimensionText, range, value: formatSelecteDate };
              onChange?.(data);
              // 确认后更新已确认的日期
              setLastConfirmedDate(checkedDate);
              setVisible(false);
              if (!selectedDate) {
                setSelectedDate(new Date())
              }
            }}
          >
            确认
          </CnButton>
        </div>
      </div>
    </CnPopup>
  );
};

export default TimePicker;